import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function DeleteOrderModal({ open, order, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  if (!open || !order) {
    return null;
  }

  const deleteOrder = async () => {
    setDeleting(true);
    try {
      const response = await api.delete(`/admin/orders/${order.id}`);
      if (response.status !== 204) {
        throw new Error("Unexpected deletion response.");
      }
      toast.success("Order permanently deleted.");
      onDeleted?.(order.id);
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Order could not be deleted.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="permanent-delete-title"
      className="fixed inset-0 z-[90] flex items-end justify-center bg-espresso/65 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      data-testid="delete-order-modal"
      onClick={(event) => {
        if (event.target === event.currentTarget && !deleting) {
          onClose?.();
        }
      }}
    >
      <div className="w-full border border-rose-300 bg-ivory sm:max-w-md">
        <div className="p-5 sm:p-7">
          <div className="flex items-center gap-2 text-rose-700">
            <AlertTriangle size={17} aria-hidden="true" />
            <span className="label-xs">Irreversible action</span>
          </div>
          <h2 id="permanent-delete-title" className="mt-3 font-serif text-3xl text-espresso">
            Permanent Delete?
          </h2>
          <p className="mt-2 text-sm text-espresso-light" data-testid="delete-order-warning">
            This cannot be undone. {order.order_number} will be removed permanently.
          </p>
          <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="btn-outline flex-1 disabled:opacity-40"
              data-testid="delete-order-modal-cancel"
            >
              Keep order
            </button>
            <button
              type="button"
              onClick={deleteOrder}
              disabled={deleting}
              className="flex flex-1 items-center justify-center gap-2 bg-rose-700 px-4 py-3 text-sm uppercase tracking-wider text-white transition-colors duration-200 hover:bg-rose-800 disabled:opacity-40"
              data-testid="delete-order-modal-confirm"
            >
              <Trash2 size={15} aria-hidden="true" />
              {deleting ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}